// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  ConfirmModal,
  Loading,
} from '~/components/common';
import { FormSendEmailModal } from '~/components/module/contact';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { RoleEnum, SearchByFilterContentEnum } from '~/utils/enum/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { IContact, IQueryDeleteContact, IQueryParamContact } from '~/utils/interface/contact';
import { IHeaderCellType } from '~/utils/interface/common';
import { formatPhoneNumber, getFullName } from '~/utils/helper/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { convertToCustomer, deleteContact, getContactList } from '~/thunks/contact/contactThunk';
import { contactActions, selectIsRefreshContactList } from '~/thunks/contact/contactSlice';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { StorageEnum } from '~/utils/enum/base';
// Styles, images, icons
import styles from './Contact.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onMail: (item: IContact) => void,
  onConvertToCustomer: (id: string) => void,
  onDelete: (id: string) => void
): IHeaderCellType<IContact>[] => {
  return [
    {
      id: 'name',
      label: t('table_head_title_name'),
      render: (record: IContact) => {
        return getFullName({ firstName: record.firstName, lastName: record.lastName });
      },
    },
    {
      id: 'email',
      label: t('table_head_title_email'),
      render: (record: IContact) => {
        return record?.email || EMPTY_STRING;
      },
    },
    {
      id: 'phone',
      label: t('table_head_title_phone'),
      render: (record: IContact) => {
        return formatPhoneNumber(record?.phoneNumber);
      },
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      render: (record: IContact) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            {...(record?.email && { onMail: () => onMail(record) })}
            {...(!record?.isConvert && {
              onConvertToCustomer: () => onConvertToCustomer(record.id),
            })}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const Contact = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/contact']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );
  const currentRole = localStorage.getItem(StorageEnum.ROLE);
  //#endregion Declare Hook

  //#region Selector
  const isRefreshContactList = useSelector(selectIsRefreshContactList);
  //#endregion Selector

  //#region Declare State
  // const [idsSelected, setIdsSelected] = useState<string[]>([]);

  const [paramObject, setParamObject] = useState<IQueryParamContact>({
    page: pageSelected,
    limit: limitPage,
  });
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contactList, setContactList] = useState<IContact[]>();
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [totalItems, setTotalItems] = useState<string>();

  const [contactSelected, setContactSelected] = useState<IContact | null>(null);
  const [isShowSendMail, setIsShowSendMail] = useState<boolean>(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [isConfirmConvertToCustomer, setIsConfirmConvertToCustomer] = useState<boolean>(false);
  const [dataIds, setDataIds] = useState<string[]>([]);
  const [contactSelectedId, setContactSelectedId] = useState<string>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setSearchKey(textSearchParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearchParams]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    handleGetContactList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshContactList) return;

    handleGetContactList(paramObject);
    dispatch(contactActions.setRefreshContactList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshContactList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleNavigateToFormContact = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createContact);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createContact);
        break;
    }
  };

  // const handleRowsSelect = (ids: string[]) => {
  //   setIdsSelected(ids);
  // };

  // const handleDeleteMultiple = () => {
  //   setDataIds(idsSelected);
  //   setIsOpenConfirm(true);
  // };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.contacts}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.contacts}/${id}`);
        break;
    }
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.contacts}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.contacts}/${id}`);
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updateContact(id));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updateContact(id));
        break;
    }
  };

  const handleShowSendMail = (data: IContact) => {
    if (!data) return;

    setContactSelected(data);
    setIsShowSendMail(true);
  };

  const handleCloseSendMail = () => {
    setContactSelected(null);
    setIsShowSendMail(false);
  };

  const handleConvertToCustomer = (id: string) => {
    if (!id) return;

    setContactSelectedId(id);
    setIsConfirmConvertToCustomer(true);
  };

  const handleConfirmConvertToCustomer = () => {
    if (!contactSelectedId) return;

    setIsLoading(true);

    dispatch(convertToCustomer(contactSelectedId))
      .unwrap()
      .then((res) => {
        dispatch(contactActions.setRefreshContactList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsConfirmConvertToCustomer(false);
        setIsLoading(false);
      });
  };

  const handleCloseConfirmConvertToCustomer = () => {
    setIsConfirmConvertToCustomer(false);
    setContactSelectedId(EMPTY_STRING);
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirm(false);
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setDataIds([id]);
    setIsOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    const params: IQueryDeleteContact = {
      deleteIds: dataIds,
    };

    dispatch(deleteContact(params))
      .unwrap()
      .then(() => {
        setDataIds([]);
        // setIdsSelected([]);
        handleCloseConfirmDelete();
        dispatch(contactActions.setRefreshContactList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleGetContactList = (params: IQueryParamContact) => {
    setIsLoading(true);

    dispatch(getContactList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;
        if (!responses) return;

        setContactList(responses);
        setPagination(pagination);
        setTotalItems(totalItems.toString());
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_contacts', { ns: 'common/sidebar' })}>
    <Box component={'div'} className={cx('container')} id='contactPage'>
      <BaseToolbar
        title={t('tile_quantity_contact_page', { quantity: totalItems })}
        textAction={t('button_add_contact')}
        onAction={handleNavigateToFormContact}
        // onDelete={handleDeleteMultiple}
        // selectedCount={idsSelected.length}
      >
        <BaseInputSearch
          searchByContent={SearchByFilterContentEnum.NAME}
          value={searchKey}
          onChange={handleSearchChange}
        />
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(
            t,
            handleView,
            handleEdit,
            handleShowSendMail,
            handleConvertToCustomer,
            handleDelete
          )}
          dataSource={contactList || []}
          // dataSelectedIds={idsSelected}
          onPageChange={handlePageChange}
          // onSelectChange={handleRowsSelect}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowSendMail && (
        <FormSendEmailModal
          dataDefault={contactSelected}
          isOpen={isShowSendMail}
          onClose={handleCloseSendMail}
        />
      )}

      {isOpenConfirm && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isConfirmConvertToCustomer && (
        <ConfirmModal
          icon={icons.commonIconConvert}
          title={t('common_title_title', { ns: 'common/base' })}
          description={t('common_title_desc', { ns: 'common/base' })}
          textConfirm={t('common_action_convert', { ns: 'common/base' })}
          onClose={handleCloseConfirmConvertToCustomer}
          onConfirm={handleConfirmConvertToCustomer}
        />
      )}

    </Box>
    </PageShell>
  );
};

export default Contact;
